import type { ActivityLogicPlugin } from '../../core/types/plugin.base.types';
import type { CartesianPlanePayload, CartesianPlaneRuntime } from '../../core/types/payloads/cartesian_plane.types';
import type { HostActionPayload, StudentInputEnvelope } from '../../core/types/events.types';
import type { RoutingMode } from '../../core/types/room.types';
import { CartesianPlanePayloadSchema } from '../../core/schemas/examGenerationSchema';

function clampGridSize(value: number): number {
  if (!Number.isFinite(value) || value <= 0) return 10;
  return Math.min(20, Math.max(1, Math.round(value)));
}

export function validateCartesianPlanePayload(raw: unknown): CartesianPlanePayload {
  const parsed = CartesianPlanePayloadSchema.safeParse(raw);
  if (!parsed.success) {
    const first = parsed.error.issues[0];
    throw new Error(first?.message || 'CartesianPlane payload invalid');
  }

  const data = parsed.data;
  const gridSize = clampGridSize(data.gridSize);
  const quadrants = data.quadrants === '4' ? '4' : '1';

  const inRange = (x: number, y: number) => {
    if (quadrants === '1') {
      return x >= 0 && x <= gridSize && y >= 0 && y <= gridSize;
    }
    return x >= -gridSize && x <= gridSize && y >= -gridSize && y <= gridSize;
  };

  const points = (data.points ?? [])
    .map((point) => ({
      x: point.x,
      y: point.y,
      ...(point.label?.trim() ? { label: point.label.trim() } : {}),
    }))
    .filter((point) => inRange(point.x, point.y));

  const polygons = (data.polygons ?? [])
    .map((polygon) => ({
      vertices: polygon.vertices.filter((vertex) => inRange(vertex.x, vertex.y)),
      ...(polygon.color?.trim() ? { color: polygon.color.trim() } : {}),
    }))
    .filter((polygon) => polygon.vertices.length >= 3);

  return {
    instruction: data.instruction.trim(),
    quadrants,
    gridSize,
    ...(points.length ? { points } : {}),
    ...(polygons.length ? { polygons } : {}),
  };
}

export function createInitialCartesianPlaneRuntime(
  _payload: CartesianPlanePayload,
  _routing: RoutingMode,
): CartesianPlaneRuntime {
  return { viewed: false };
}

export const cartesianPlaneLogic: ActivityLogicPlugin<'cartesian_plane'> = {
  mode: 'cartesian_plane',
  validatePayload: validateCartesianPlanePayload,
  createInitialRuntime: createInitialCartesianPlaneRuntime,

  onStudentInput(ctx, input: StudentInputEnvelope) {
    if (input.action === 'EXAM_CONTINUE' || input.action === 'VIEW') {
      return { runtime: { ...ctx.runtime, viewed: true } };
    }
    return { runtime: ctx.runtime, rejected: true, rejectReason: 'UNKNOWN_ACTION' };
  },

  onHostAction(ctx, _action: HostActionPayload) {
    return { runtime: ctx.runtime };
  },
};

export function demoCartesianPlanePayload(): CartesianPlanePayload {
  return validateCartesianPlanePayload({
    instruction: 'Ubica los vértices del triángulo y lee sus coordenadas.',
    quadrants: '1',
    gridSize: 10,
    points: [
      { x: 2, y: 3, label: 'A' },
      { x: 6, y: 3, label: 'B' },
      { x: 4, y: 7, label: 'C' },
    ],
    polygons: [
      {
        vertices: [
          { x: 2, y: 3 },
          { x: 6, y: 3 },
          { x: 4, y: 7 },
        ],
        color: '#8A7BFA',
      },
    ],
  });
}
